import { authenticatedFetch } from './api';

const API_ALERT_RULES_URL = '/alert-rules'; // Relative to API base

/**
 * Fetches alert rules with pagination and filters.
 * @param {object} [params={}] e.g., { page, limit, sortBy, sortOrder, sensor_id, is_enabled, search }
 * @returns {Promise<any>}
 */
export const fetchAlertRules = async (params = {}) => {
  const queryParams = new URLSearchParams();
  for (const key in params) {
    if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
      queryParams.append(key, params[key]);
    }
  }
  const endpoint = `${API_ALERT_RULES_URL}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  console.log(`[AlertRuleService] Fetching alert rules from: ${endpoint}`);
  try {
    const responseData = await authenticatedFetch(endpoint);
    return responseData || { data: [], pagination: {} }; // Ensure data and pagination always exist
  } catch (error) {
    console.error("[AlertRuleService] Error fetching alert rules:", error);
    throw error;
  }
};

/**
 * Fetches a single alert rule by ID.
 * @param {number} ruleId
 * @returns {Promise<any>}
 */
export const fetchAlertRuleById = async (ruleId) => {
  if (!ruleId) throw new Error("Rule ID is required.");
  const endpoint = `${API_ALERT_RULES_URL}/${ruleId}`;
  console.log(`[AlertRuleService] Fetching alert rule ID: ${ruleId}`);
  try {
    return await authenticatedFetch(endpoint);
  } catch (error) {
    console.error(`[AlertRuleService] Error fetching alert rule ${ruleId}:`, error);
    throw error;
  }
};

/**
 * Creates a new alert rule.
 * @param {object} ruleData
 * @returns {Promise<any>}
 */
export const createAlertRule = async (ruleData) => {
  const endpoint = API_ALERT_RULES_URL;
  console.log("[AlertRuleService] Creating new alert rule:", ruleData);
  const options = {
    method: 'POST',
    body: JSON.stringify(ruleData),
  };
  try {
    return await authenticatedFetch(endpoint, options);
  } catch (error) {
    console.error("[AlertRuleService] Error creating alert rule:", error);
    throw error;
  }
};

/**
 * Updates an existing alert rule.
 * @param {number} ruleId
 * @param {object} ruleData
 * @returns {Promise<any>}
 */
export const updateAlertRule = async (ruleId, ruleData) => {
  if (!ruleId) throw new Error("Rule ID is required for update.");
  const endpoint = `${API_ALERT_RULES_URL}/${ruleId}`;
  console.log(`[AlertRuleService] Updating alert rule ID ${ruleId}:`, ruleData);
  const options = {
    method: 'PUT',
    body: JSON.stringify(ruleData),
  };
  try {
    return await authenticatedFetch(endpoint, options);
  } catch (error) {
    console.error(`[AlertRuleService] Error updating alert rule ${ruleId}:`, error);
    throw error;
  }
};

/**
 * Deletes an alert rule.
 * @param {number} ruleId
 * @returns {Promise<any>}
 */
export const deleteAlertRule = async (ruleId) => {
  if (!ruleId) throw new Error("Rule ID is required for deletion.");
  const endpoint = `${API_ALERT_RULES_URL}/${ruleId}`;
  console.log(`[AlertRuleService] Deleting alert rule ID: ${ruleId}`);
  const options = {
    method: 'DELETE',
  };
  try {
    return await authenticatedFetch(endpoint, options);
  } catch (error) {
    console.error(`[AlertRuleService] Error deleting alert rule ${ruleId}:`, error);
    throw error;
  }
};

/**
 * Fetches all AI-suggested alert rules that are pending review.
 * @returns {Promise<Array>} A promise that resolves to an array of rule objects.
 */
export const fetchAiSuggestedRules = async () => {
  try {
    const data = await authenticatedFetch('/alert-rules/ai-suggestions');
    
    // --- DEBUG START ---
    console.log('[AI-SUGGEST-DEBUG-SERVICE] Raw data received from API:', data);
    // --- DEBUG END ---

    // Note: Your controller is likely sending { success: true, data: [...] }
    // This service should return data.data
    if (data && data.success) {
      return data.data;
    }
    // Fallback for if it just returns the array (which it might be)
    return data; 
  } catch (error) {
    console.error('Error in fetchAiSuggestedRules service:', error);
    throw error;
  }
};